/**
 * R58-L-15/L-16/L-17 — Viewer + readouts assembly for the analysis surface.
 *
 * Owns the local state (cursor pixel + zoom %) that the readouts panel needs,
 * and forwards every other prop to the `Viewer` primitive in `@aeriosiq/ui`.
 *
 * Max-zoom and the debug-overlay toggle are sourced from the user setting
 * (`useViewerSettings`) — both are persisted under `auth/v1@4.7.0`'s
 * `LysePreferences.viewer` block.
 *
 * SB6-ANA-VIEW-03 — the radius readout is sourced from the new
 * `useRadiusReadout` hook (image-centre stand-in until Story 032's
 * pic2comp ships). The readout is now visible in ALL data acquisition
 * states (including LOCKED) — the parent no longer has to pass
 * `radiusMeters={undefined}` to suppress it.
 */
import { useCallback, useRef, useState } from "react";
import { Box } from "@mui/material";
import {
  Viewer,
  type BoundingBox,
  type ViewportState,
  type ViewerPolygon,
  type ViewerViewportApi,
} from "@aeriosiq/ui";
import type { ImageRef } from "@aeriosiq/types";
import { ViewerReadouts } from "./ViewerReadouts.js";
import { useViewerSettings } from "./useViewerSettings.js";
import { useRadiusReadout } from "./useRadiusReadout.js";
import { useGpuDownscaleWarning } from "./useGpuDownscaleWarning.js";

export interface AnalysisViewerPanelProps {
  imageUrl: string | null;
  progressiveImageUrl?: string | null;
  fallbackImageUrl?: string | null;
  ariaLabel: string;
  boundingBoxes: BoundingBox[];
  selectedBoxId: string | null;
  onBoxClick: ((id: string) => void) | undefined;
  initialViewport: ViewportState | null;
  onViewportChange: (state: ViewportState) => void;
  /**
   * SB6-ANA-VIEW-03 — Active image. Passed so the panel can call
   * `useRadiusReadout` locally instead of every consumer passing a
   * pre-computed `radiusMeters`. `null` → no active image; the readout
   * row hides (`undefined` semantic in ViewerReadouts).
   */
  activeImage?: ImageRef | null;
  /** SETTINGS-02 — zoom-aware label declutter (from bbox prefs). */
  autoHideLabelsWhenDense?: boolean;
  /** SETTINGS-02 — copy for the top-centre "labels hidden" notice. */
  denseLabelNotice?: string;
  /**
   * ANA-R3-NAV-01 — when provided, the Viewer's four arrow keys step the
   * active data point (Up/Left → previous, Down/Right → next) instead of
   * panning the image. Wired by the analysis surface to
   * `useDataPointNavigation`.
   */
  onArrowNavigate?: (direction: "previous" | "next") => void;
  /**
   * ANA-R3-GEOM-03 — free-form defect outlines (normalised paths) rendered over
   * the image as true polygons, with the bbox `<Rect>` fallback handled by the
   * caller via `boundingBoxes` (a defect is in exactly one of the two lists).
   */
  polygonOverlays?: ViewerPolygon[];
  /**
   * ANA-R3-GEOM-03 — receives the live OSD viewport projection so the sibling
   * draw overlay can build a CoordinateTransform. Forwarded straight to the
   * Viewer primitive.
   */
  onViewportApiReady?: (api: ViewerViewportApi | null) => void;
}

export function AnalysisViewerPanel({
  imageUrl,
  progressiveImageUrl = null,
  fallbackImageUrl = null,
  ariaLabel,
  boundingBoxes,
  selectedBoxId,
  onBoxClick,
  initialViewport,
  onViewportChange,
  activeImage = null,
  autoHideLabelsWhenDense = false,
  denseLabelNotice,
  onArrowNavigate,
  polygonOverlays = [],
  onViewportApiReady,
}: AnalysisViewerPanelProps): React.ReactElement {
  const { viewer: viewerPrefs } = useViewerSettings();
  const [cursorPixel, setCursorPixel] = useState<{
    u: number;
    v: number;
  } | null>(null);
  // Story 032 — normalised cursor (fraction of the image, resolution-
  // independent) drives the live radius transform.
  const [cursorNorm, setCursorNorm] = useState<{
    nx: number;
    ny: number;
  } | null>(null);
  // L-15 AC5 — zoom % starts at 100 (fit-to-viewport) before OSD emits
  // its first viewport-change. The Viewer publishes the real value on
  // open.
  const [zoomPercent, setZoomPercent] = useState<number>(100);

  // Story 032 AC5 — throttle the radius-driving cursor updates to at most
  // one per 50 ms. The transform itself is cheap (<1 ms / call); the throttle
  // just caps re-render churn on a fast mouse. `null` (mouse-out) passes
  // through immediately so the readout reverts to the image-centre value.
  const lastNormAtRef = useRef(0);
  const handleCursorNorm = useCallback(
    (norm: { nx: number; ny: number } | null) => {
      if (norm === null) {
        setCursorNorm(null);
        return;
      }
      const now = performance.now();
      if (now - lastNormAtRef.current >= 50) {
        lastNormAtRef.current = now;
        setCursorNorm(norm);
      }
    },
    [],
  );

  // SB6-ANA-VIEW-03 / Story 032 — live distance-from-root readout. Updates per
  // cursor move via the pic2comp transform. When activeImage is null we pass
  // `undefined` so the row hides; otherwise we render the number (or `—` when
  // geometry is unavailable).
  const radiusValue = useRadiusReadout({ activeImage, cursorNorm });
  const radiusMeters = activeImage == null ? undefined : radiusValue;

  // PERF-GPU-01 (decision N-7) — per-image downscale warning: the device
  // max-texture is detected once per session (Viewer init, memoised); the
  // predicate re-evaluates per active image so mixed-resolution datasets
  // flip the indicator on navigation.
  const downscaleWarning = useGpuDownscaleWarning(activeImage);

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      <Viewer
        imageUrl={imageUrl}
        progressiveImageUrl={progressiveImageUrl}
        fallbackImageUrl={fallbackImageUrl}
        ariaLabel={ariaLabel}
        boundingBoxes={boundingBoxes}
        selectedBoxId={selectedBoxId}
        // `exactOptionalPropertyTypes` requires we either omit
        // `onBoxClick` or pass a concrete fn. Forward only when
        // defined; the Viewer treats a missing handler as "boxes
        // non-interactive" anyway.
        {...(onBoxClick ? { onBoxClick } : {})}
        initialViewport={initialViewport}
        onViewportChange={onViewportChange}
        maxZoomPercent={viewerPrefs.maxZoomPercent}
        onCursorImagePixel={setCursorPixel}
        onCursorImageNormalized={handleCursorNorm}
        onZoomPercentChange={setZoomPercent}
        autoHideLabelsWhenDense={autoHideLabelsWhenDense}
        {...(denseLabelNotice !== undefined ? { denseLabelNotice } : {})}
        {...(onArrowNavigate ? { onArrowNavigate } : {})}
        polygonOverlays={polygonOverlays}
        {...(onViewportApiReady ? { onViewportApiReady } : {})}
      />
      <ViewerReadouts
        cursorPixel={cursorPixel}
        zoomPercent={zoomPercent}
        radiusMeters={radiusMeters}
        showDebugOverlay={viewerPrefs.showDebugOverlay}
        downscaleWarning={downscaleWarning}
      />
    </Box>
  );
}
